import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  inject
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from "@ngx-formly/core";
import _ from "lodash";
import { MessageService } from "primeng/api";
import { Observable, map, of, take } from "rxjs";
import * as uuid from 'uuid';
import { INZorroOutput } from "../../../core/models/common";
import { TestCaseGeneratorActions } from "../../../core/ngrx/actions/testcase-generator.actions";
import {
  selectConnections
} from "../../../core/ngrx/selectors/connection.selectors";
import {
  selectAllTestCaseVersions,
  selectAllTestCasesByVersionId,
  selectError,
  selectLoading,
  selectLoadingTestCases,
  selectVersionIdState,
} from "../../../core/ngrx/selectors/testcase-generator.selectors";
import { CommonService } from "../../../core/services/common.service";
import { ExcelExportService } from "../../../core/services/download-excel/excel-export.service";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { UtilitiesService } from "../../../core/services/utilities.service";
import { MaterialModule } from "../../../core/shared/material.module";
import { testCaseByVersionTable } from "../../../core/table-templates/test-case-by-version-table";
import { testCaseVersionMoreDetails, testCaseVersionTableColumns } from "../../../core/table-templates/test-case-table";
import { MessageEnum } from "../../../core/utils/message.enum";
import { ZorroTableComponent } from "../../common/custom-table/zorro-table.component";
import { AddMissingTestCaseModalComponent } from "../../modal-templates/add-missing-test-case-modal/add-missing-test-case-modal.component";
import { DeleteConfirmationComponent } from "../../modal-templates/delete-confirmation/delete-confirmation.component";
import { SharedComponents } from "../../shared.module";
import { TestCaseTableComponent } from "../test-case-table/test-case-table.component";

/**
 * Component for managing and displaying test cases by version.
 */
@Component({
  selector: "app-test-case-version",
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    FormlyModule,
    FormlyBootstrapModule,
    TestCaseTableComponent,
    ...SharedComponents,
  ],
  templateUrl: "./test-case-version.component.html",
  styleUrl: "./test-case-version.component.scss",
})
export class TestCaseVersionComponent {
  @ViewChild(ZorroTableComponent) zorroTableComponent!: ZorroTableComponent;
  private _testCaseTableComponent!: TestCaseTableComponent;
  @ViewChild(TestCaseTableComponent) set testCaseTableComponent(
    component: TestCaseTableComponent
  ) {
    if (component) {
      this._testCaseTableComponent = component;
    }
  }
  /** Input property for passing data to the component. */
  @Input() data!: any;
  private messageService = inject(MessageService);
  /** Observable to manage testcases. */
  testCasesByVersionId$!: Observable<any[]>;
  testCaseVersions$!: Observable<any[]>;
  loading$!: Observable<boolean>;
  loadingTestCases$!: Observable<boolean>;
  error$!: Observable<string | null>;
  tableDataSet$!: Observable<any[]>;

  /** Form group for version history. */
  form = new FormGroup({});
  versionHistoryModel: any = {};
  options: FormlyFormOptions = {};
  versionHistoryField: FormlyFieldConfig[] = [];
  isGenerate: boolean = false;
  selectedTestCaseCount:any = 0;
  /** Data structure for table configuration. */
  tableData: {
    headers?: string;
    columns: any[];
    data: Observable<any[]>;
  } = {
    headers: "",
    columns: [],
    data: of([]),
  };
  projectId: string;
  showNoData: boolean = false;
  private modalService = inject(NgbModal);

  selectedRow: any;
  versionId: any;
  selectedVersionData: any;
  llmConnection: any;
  storyIds:any;
  responseData: any =[];
  responseDataCloned: any;
  isRowEditing = false;
  restStatus: any = {
    save: false,
    delete: false
  };

  selectedItem: any;
  tableConfigs: any = {
      columns: [],
      rows: [],
      titleKey: "testScenario", //to highlight the title content
      isEdit: true, //edit button on top right, editable fields
      isFeedback: false, //to add feedback
    };
  pageSize: number = 5; // Default page size

  /**
   * Constructor to initialize services and load initial data.
   * @param store - NGRX Store for state management.
   * @param route - ActivatedRoute for accessing route parameters.
   * @param cdr - ChangeDetectorRef for manual change detection.
   * @param utilitiService - UtilitiesService for helper functions.
   * @param excelExportService - ExcelExportService for exporting data.
   */
  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private utilitiService: UtilitiesService,
    private excelExportService: ExcelExportService,
    private restService: RestService,
    private commonService: CommonService,
    private readonly toaster: ToasterService
  ) {
    this.projectId = this.route.snapshot.paramMap.get("id") as string;

    // Load Store Data and set values
    this.loadStoreData();

    // Initilaize form data
    this.setupFields();
  }

  /** Lifecycle hook to initialize component data and states. */
  ngOnInit() {
    this.tableData = this.getData("testCasesByVersion");
    // this.tableData?.data.pipe(
    //   map((data: any[]) => 
    //     data.filter((item, index, self) => 
    //       index === self.findIndex(i => i.key === item.key)
    //     ).map(item => item.key)
    //   )
    // ).subscribe(ids => {
    //   this.storyIds = ids;
    // });


    // this.tableData.data = response;
    
    this.utilitiService.dispatchIfEmpty(
      this.testCaseVersions$,
      TestCaseGeneratorActions.loadTestCaseVersions({
        projectId: this.projectId,
      })
    );
    this.displayNoDataWithDelay(2500);

    // to get the version id
    this.store.select(selectVersionIdState).subscribe((versionId: any) => {
      this.versionId = versionId;
    });

    // to trigger getting latest added test suite
    this.commonService.versionUpdate$.subscribe(() => {
      this.loadDataAfterPersist();
    });
    // Trigger change detection manually
    this.cdr.detectChanges();
  }

  loadDataAfterPersist() {
    this.tableData = this.getData("testCasesByVersion");
    // this.tableData?.data.pipe(
    //   map((data: any[]) => 
    //     data.filter((item, index, self) => 
    //       index === self.findIndex(i => i.key === item.key) 
    //     ).map(item => item.key) 
    //   )
    // ).subscribe(ids => {
    //   this.storyIds = ids;
    // });
    
    
    // this.utilitiService.dispatchIfEmpty(
    //   this.testCaseVersions$,
    this.store.dispatch(
      TestCaseGeneratorActions.loadTestCaseVersions({
        projectId: this.projectId,
      })
    );
    // );
    this.displayNoDataWithDelay(2500);
    this.store.select(selectVersionIdState).subscribe((versionId: any) => {
      this.versionId = versionId;
    });
    // Load Store Data and set values
    this.loadStoreData();
    // Initilaize form data
    this.setupFields();
    // Trigger change detection manually
    this.cdr.markForCheck();
  }

  /**
   * Delays displaying the "no data" message.
   * @param delay - Time in milliseconds to delay the message.
   */
  displayNoDataWithDelay(delay: number): void {
    setTimeout(() => {
      this.showNoData = true;
      this.testCaseVersions$.pipe(take(1)).subscribe((versionConnections) => {
        if (
          !this.versionHistoryModel.versions &&
          versionConnections.length > 0
        ) {
          this.versionHistoryModel.versions = versionConnections[0].value;
          this.versionHistoryModel.llmConnection =
            versionConnections[0].value.llmName;
          this.versionHistoryModel.timeStamp =
            versionConnections[0].value.timestamp;
          this.form.patchValue({
            versions: this.versionHistoryModel.versions,
            llmConnection: this.versionHistoryModel.llmConnection,
            timeStamp: this.versionHistoryModel.timeStamp,
          });
          this.utilitiService.dispatchIfEmpty(
            this.testCasesByVersionId$,
            TestCaseGeneratorActions.loadTestCaseDetailsByVersionId({
              projectId: this.projectId,
              versionId: this.versionHistoryModel?.versions?._id,
            })
          );
        }
      });
    }, delay);
  }

  /** Loads data from the store and initializes observables. */
  loadStoreData() {
    this.testCasesByVersionId$ = this.store.select(
      selectAllTestCasesByVersionId
    );
    this.store.select(selectAllTestCasesByVersionId).subscribe((res: any) => {
      this.responseData = res.map((item: any) => {
        return { ...item, id: item.id ||  uuid.v4() }
      });
      this.responseDataCloned = _.cloneDeep(this.responseData);
      this.storyIds = [...new Set(this.responseDataCloned.map((item: any) => item.key))];
      this.getData('');
      // Trigger change detection to update child component
      this.cdr.detectChanges();
    });
    this.loading$ = this.store.select(selectLoading);
    this.loadingTestCases$ = this.store.select(selectLoadingTestCases);
    this.error$ = this.store.select(selectError);

    this.testCaseVersions$ = this.store.select(selectAllTestCaseVersions).pipe(
      map((connections) =>
        connections.map((connection: any) => ({
          value: connection,
          label: connection.versionName,
        }))
      )
    );
  }

  /** Exports test case data to an Excel file. */
  onDownload() {
    if (this.selectedRow?.length) {
      // const formattedData = this.selectedRow?.map((row: any) => ({
      //   "Story ID": row.key,
      //   Story: row.storydesc,
      //   "TC ID": row.testCaseId,
      //   "TC Description": row.testCase,
      //   Prerequisites: row.prerequisites,
      //   "Test Steps": row.testScenario,
      //   "Expected Results": row.expectedResult,
      // }));
      // const wrapColumns = ["Prerequisites", "TestSteps", "ExpectedResult"];
      // this.excelExportService.exportToExcel(
      //   formattedData,
      //   this.versionHistoryModel?.versions?.versionName,
      //   "Test Cases",
      //   wrapColumns
      // );

      const formattedData = this.selectedRow?.map((row: any) => ({
        _id: row._id,
        key: row.key || '',
        storydesc: row.storydesc || '',
        testCaseId: row.testCaseId || '',
        testCase: row.testCase || '',
        prerequisites: row.prerequisites || '',
        testScenario: row.testScenario || '',
        expectedResult: row.expectedResult || '',
        storysummary:row?.storysummary || '',
        // priority:row?.priority || '',
        testCaseType:row?.testCaseType || row?.testType || '',
        subType:row?.subType || '',
      }));

      const payload = {
        testSuiteName: this.versionHistoryModel?.versions?.versionName,
        selectedTestCaseCount: formattedData.length,
        testCaseReportDetails: formattedData,
      };
      this.restService.downloadExcel(this.projectId, payload).subscribe({
        next: (response: any) => {
          window.open(response?.data, "_blank");
        },
        error: (error: any) => {
          let errMessage = error?.message ? error?.message : MessageEnum.EXCEL_DOWNLOAD_FAILURE;
          this.toaster.pop({ type: 'error', text: errMessage});
        }
      });
    }
  }

  /** Submits the selected version history. */
  onSubmitVersionHistory() {
    this.selectedTestCaseCount = 0;
    this.store.dispatch(
      TestCaseGeneratorActions.loadTestCaseDetailsByVersionId({
        projectId: this.projectId,
        versionId: this.versionHistoryModel?.versions?._id,
      })
    );
    this.getllmAndtimestamp();
  }

  getllmAndtimestamp() {
    const selectedVersion: any = this.form.get("versions")?.value;

    this.testCaseVersions$.pipe(take(1)).subscribe((versionConnections) => {
      if (versionConnections.length > 0) {
        // Find the selected version
        const selectedVersionData = versionConnections.find(
          (connection: any) =>
            connection.value.versionName === selectedVersion?.versionName
        );

        this.selectedVersionData = selectedVersionData;
        if (selectedVersionData) {
          // Update versionHistoryModel based on the selected version
          this.versionHistoryModel.llmConnection =
            selectedVersionData.value.llmName;
          this.versionHistoryModel.timeStamp =
            selectedVersionData.value.timestamp;

          this.form.patchValue({
            llmConnection: this.versionHistoryModel.llmConnection,
            timeStamp: this.versionHistoryModel.timeStamp,
          });
        }
      }
    });
  }

  /** Sets up Formly fields for version history. */
  setupFields() {
    this.versionHistoryField = [
      {
        key: "versions",
        type: "select",
        templateOptions: {
          label: "Test Suites",
          placeholder: "Select Test Suites",
          options: this.testCaseVersions$,
          required: true,
        },
      },
      {
        key: "llmConnection",
        type: "input",
        templateOptions: {
          label: "LLM Name",
          placeholder: "LLM Name",
          // options: this.llmConnections$,
          // required: true,
          disabled: true,
        },
      },
      {
        key: "timeStamp",
        type: "input",
        templateOptions: {
          label: "Timestamp",
          placeholder: "Timestamp",
          disabled: true,
          // options: this.jiraConnections$,
        },
      },
    ];
  }

  /**
   * Retrieves table data based on the type.
   * @param type - Type of data to retrieve.
   * @returns Table data object.
   */
  getData(type: string) {   
    this.tableConfigs = {
      columns: [...testCaseVersionTableColumns],
      rows: [...this.responseData],
      titleKey: "testCase",
      isEdit: true,
      isFeedback: false,
      moreDetails: testCaseVersionMoreDetails,
    };

    this.selectedItem = this.responseData[0];
    return {
      headers: "Generated Test Cases",
      columns: testCaseByVersionTable,
      data: this.testCaseVersions$
    };
  }
  

  makeRowEditableById(id: string, list: any[], value: boolean) {
    let isResolved = false;
    list.forEach((ele: any, ind: number) => {
      if (ele.id === id) {
        isResolved = true;
        list[ind].isRowEditable = value;
        // Update selected item list
        let selectedInd = this.selectedRow?.findIndex((item: any) => item._id === list[ind]._id);
        if(selectedInd >= 0 && !value) {
          let item = _.cloneDeep(list[ind]);
          delete item.isRowEditable;
          delete item.id;
          this.selectedRow[selectedInd] = item;
        }
      }
      if (!isResolved && ele.children?.length) {
        this.makeRowEditableById(id, ele.children, value);
      }
    });
    if (isResolved) this.zorroTableComponent.refreshTableData();
  }

  onTableActinChange(event: INZorroOutput) {
    let rowId = event.data.row.id;
    if (event.data.columnConfig?.value === "edit") {
      this.isRowEditing = true;
      this.makeRowEditableById(rowId, this.responseData, true);
    }
    if (event.data.columnConfig?.value === "check") {
      this.makeRowEditableById(rowId, this.responseData, false);
    }
    if (event.data.columnConfig?.value === "close") {
      let originalItem = this.responseDataCloned.find((ele: any) => ele.id === rowId);
      let ind = this.responseData.findIndex((ele: any) => ele.id === rowId);
      this.responseData[ind] = _.cloneDeep(originalItem);
      this.makeRowEditableById(rowId, this.responseData, false);
    }
    this.isRowEditing = this.responseData.some((ele: any) => ele.isRowEditable);
  }

  handleRowSelection(event: any) {
    this.selectedRow = [];
    if (event.data) {
      let items = _.cloneDeep(event.data);
      items.forEach((ele: any) => {
        delete ele.id;
        delete ele.isRowEditable;
      });
      this.selectedRow = items;
    }
    this.selectedRow = event.data.filter((record: any) => record.select);
    this.selectedTestCaseCount = this.selectedRow.length == 0 ? 0 : this.selectedRow.length.toString().padStart(2, '0');
  }

  saveTestCases(testCases: any) {
    // if (!this.selectedRow) {
    //   this.toaster.pop({ type: 'error', text: MessageEnum.DATA_SELECTION_NONE});
    //   return;
    // }
    // // remove id , select
    // this.selectedRow = this.selectedRow.map(
    //   (item: { id?: string; [key: string]: any }) => {
    //     const { id, select, feedbackEnabled, iconClicked, isRowEditable, ...rest } = item;
    //     return rest;
    //   }
    // );

    this.restStatus.save = true;
    this.restService
      .editTestCase(
        this.projectId,
        this.versionHistoryModel?.versions?._id,
        testCases
      )
      .subscribe({
        next: (response: any) => {
          if (response?.status === 201) {
            this.toaster.pop({ type: 'success', text: MessageEnum.TEST_CASE_UPDATED_SUCCESS});
          }
          this.restStatus.save = false;
          const updatedItem = testCases[0];

          this.responseData = this.responseData.map((item:any) =>
            item.id === updatedItem.id ? updatedItem : item
          );
          
          this.tableConfigs.rows = this.tableConfigs.rows.map((item:any) =>
            item.id === updatedItem.id ? updatedItem : item
          );
          this.loadingTestCases$ = of(false);
          // this.getData("");
          // this.loadDataAfterPersist();
        },
        error: (error: any) => {
          console.error("Edit API Error:", error);
          this.restStatus.save = false;
          // let errorMessage = error?.message;
          // if (errorMessage.startsWith('Error: ')) {
          //   errorMessage = errorMessage.substring(7); // Remove "Error: " prefix
          // }
          this.toaster.pop({ type: 'error', text: error?.message || MessageEnum.TEST_CASE_UPDATED_FAILURE});
        }
      });
  }

  deleteTestCases() {
    if (!this.selectedRow || Object.keys(this.selectedRow).length === 0) {
      this.toaster.pop({ type: 'error', text: MessageEnum.DATA_SELECTION_NONE});
      return;
    }
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });
    modalRef.result.then((result: any) => {
      if (result) {
        this.removeTestCase(this.projectId);
      }
    });
  }

  removeTestCase(projectId: any) {
    // remove id , select
    this.commonService.notifyDelete(this.selectedRow);
    this.selectedRow = this.selectedRow.map(
      (item: { id?: string; [key: string]: any }) => {
        const { id, select, feedbackEnabled, iconClicked, isRowEditable, ...rest } = item;
        return rest;
      }
    );

    this.restStatus.delete = true;
    this.loadingTestCases$ = of(true);
    this.restService
      .deleteTestCase(
        projectId,
        this.versionHistoryModel?.versions?._id,
        this.selectedRow
      )
      .subscribe({
        next: (response: any) => {
          console.log("Delete Response:", response);
          
          if (response?.status === 201) {
            const skipped = response?.data?.skippedTestcases || [];
            const deleted = response?.data?.deletedTestcases || [];

        this.selectedRow.forEach((row: any) => {
        const id = row._id;

              if (skipped.includes(id)) {
                this.toaster.pop({
                  type: "error",
                  text: MessageEnum.TEST_CASE_ALREADY_MAPPED,
                });
              } else if (deleted.includes(id)) {
                this.toaster.pop({
                  type: "success",
                  text: MessageEnum.TEST_CASE_DELETION_SUCCESS,
                });
                let ind = this.tableConfigs?.rows?.findIndex(
                  (item: any) => item._id === row._id
                );
                this.tableConfigs?.rows?.splice(ind, 1);
              }
            });

            this.loadingTestCases$ = of(false);
            // this.getData("");
            // this.loadDataAfterPersist();
          }
          this.restStatus.delete = false;
        },
        error: (error: any) => {
          console.error("Delete API Error:", error);
          this.restStatus.delete = false;
          this.loadingTestCases$ = of(false);
          this.toaster.pop({
            type: "error",
            text: MessageEnum.TEST_CASE_DELETION_FAILURE,
          });
        },
      });
  }

  addMissingTestCases() {
    this.store.select(selectConnections).subscribe((connections: any) => {
      connections.LLM.map((connection: any) => {
        if (
          connection.connectionName ==
          this.versionHistoryModel?.versions?.llmName
        ) {
          this.llmConnection = connection;
          console.log("connection", connection);
        }
      });
    });

    const configs = {
      title: "Add Missing Test Cases",
      data:{
        storyIds: this.storyIds
      }
    };
    let modalRef = this.modalService.open(AddMissingTestCaseModalComponent, {
      centered: true,
      size: "md",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = configs;
    modalRef.result.then((result: any) => {
      if (result) {
        this.addTestCase(result?.value);
      }
    });
  }

  addTestCase(value: any) {
    let obj = {
      testCaseId: value?.testCaseId,
      testCase: value?.testCaseDescription,
      testScenario: value?.testSteps,
      expectedResult: value?.expectedResult,
      prerequisites: value?.prerequisites,
      steps: value?.testSteps,
      sanitizedTestSteps: value?.sanitizedTestSteps ?? "",
      key: value?.storyId,
      storydesc: value?.userStory,
      storysummary: value?.userStory
    };

    const payload = {
      versionName: this.versionHistoryModel?.versions?.versionName,
      selectedVersionId: this.versionHistoryModel?.versions?._id,
      llmConnectionId: this.llmConnection?.id,
      isNewTestSuite: false,
      testCases: [
        obj
      ],
    };

    this.loadingTestCases$ = of(true);
    this.restService.addMissingTestCase(this.projectId, payload).subscribe({
      next: (response: any) => {
        this.toaster.pop({ type: 'success', text: MessageEnum.TEST_CASE_CREATION_SUCCESS});
        this.store.dispatch(
          TestCaseGeneratorActions.loadTestCaseDetailsByVersionId({
            projectId: this.projectId,
            versionId: this.versionHistoryModel?.versions?._id,
          })
        );
        setTimeout(() => {
          if (this._testCaseTableComponent) {
            this._testCaseTableComponent.updatePageSize(this.pageSize);
          }
        });
        this.loadingTestCases$ = of(false);
        // this.zorroTableComponent.refreshTableData();
        // this.getData("");
        // this.loadDataAfterPersist();
      },
      error: (error: any) => {
        this.loadingTestCases$ = of(false);
        this.toaster.pop({ type: 'error', text: MessageEnum.TEST_CASE_CREATION_FAILURE});
      }
    });
  }

  handleSidePanelActions(data: any) {
    switch (data.type) {
      case "save":
        let { id, ...rest } = data.data;
        this.saveTestCases([rest]);
        break;

      case "row-click":
        this.selectedItem = data?.data?.row;
        break;

      default:
        break;
    }
    
  }

  onPageChange(event: any){
    this.pageSize = event.size !=undefined ? event.size : this.pageSize;
  }
}
